import {Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {BehaviorSubject, Observable, tap} from "rxjs";
import {NycAuth, UserOverview, UserOverviewSearchResult} from "../model/user.model";
import {environment} from "../environments/environment";
import {
  CheckEligibilityCommand,
  CheckEligibilityResultDto,
  ConnectToNycApplicationCommand,
  FixNycProfileResultDto,
  LinkAuthToUserCommand,
  MarkAsPaidCommand,
  NycApplicationOverrideDto,
  UpsertNycApplicationOverrideCommand,
  SendCustomNotificationCommand,
  SendInAppNotificationCommand,
  SendNotificationCommand,
  ToggleIgnoreCommand,
  UpdateNycAuthCommand,
  UpdateStripeStatusCommand,
  UserDto,
  UserRegistrationDto,
  RegistrationSearchResultDto,
  VerifyNycHouseholdResultDto
} from "admin-api-dto";

@Injectable({
  providedIn: 'root'
})
export class UserService {
  get users$(): Observable<UserOverview[]> {
    return this._users$.asObservable();
  }

  private readonly _users$ = new BehaviorSubject<UserOverview[]>([]);

  constructor(private readonly http: HttpClient) {
  }


  deleteUser(id: string) {
    return this.http.delete(`${environment.apiUrl}/api/private/users/${id}`).pipe();
  }

  redistributeIncome(id: string) {
    return this.http.put(`${environment.apiUrl}/api/private/users/${id}/redistribute-income`, {}).pipe(
    );
  }

  linkAuthToOtherUser(id: string, command: LinkAuthToUserCommand) {
    return this.http.put<{
      updatedMappings: number;
      sourceUserId: string;
      targetUserId: string;
      deleted: boolean;
    }>(`${environment.apiUrl}/api/private/users/${id}/link-auth`, command);
  }

  loginWith(id: string) {
    return this.http.post<{ url: string }>(`${environment.apiUrl}/api/private/users/${id}/login-with`, {}).pipe(
      tap((data) => {
        window.open(
          data.url
        )
      })
    )
  }

  markAsRegisteredInNyc(id: string) {
    return this.http.put(`${environment.apiUrl}/api/private/users/${id}/mark-as-registered-in-nyc`, {}).pipe(
    );
  }

  updateNycProfileAuth(id: string, command: UpdateNycAuthCommand) {
    return this.http.put(`${environment.apiUrl}/api/private/users/${id}/update-nyc-profile-auth`, command).pipe(
    );
  }

  getUser(id: string): Observable<UserDto> {
    return this.http.get<UserDto>(`${environment.apiUrl}/api/private/users/${id}`);
  }

  requestCsvExport() {
    return this.http.get<{ s3Key: string }>(`${environment.apiUrl}/api/private/users/csv`);
  }

  getCsvDownloadUrl(s3Key: string) {
    return this.http.get<{ url: string }>(`${environment.apiUrl}/api/private/users/csv/signed`, {
      params: {s3Key}
    });
  }

  requestCancelEventsExport() {
    return this.http.get<{ s3Key: string }>(`${environment.apiUrl}/api/private/cancel-data/events-csv`);
  }

  getCancelEventsDownloadUrl(s3Key: string) {
    return this.http.get<{ url: string }>(`${environment.apiUrl}/api/private/cancel-data/events-csv/signed`, {
      params: {s3Key}
    });
  }

  requestUnknownEmailsExport() {
    return this.http.get<{ s3Key: string }>(`${environment.apiUrl}/api/private/emails/unknown-csv`);
  }

  getUnknownEmailsDownloadUrl(s3Key: string) {
    return this.http.get<{ url: string }>(`${environment.apiUrl}/api/private/emails/unknown-csv/signed`, {
      params: {s3Key}
    });
  }

  requestCustomerDataExport() {
    return this.http.get<{ s3Key: string }>(`${environment.apiUrl}/api/private/users/customer-data-csv`);
  }

  getCustomerDataDownloadUrl(s3Key: string) {
    return this.http.get<{ url: string }>(`${environment.apiUrl}/api/private/users/customer-data-csv/signed`, {
      params: {s3Key}
    });
  }

  getNycAuth(id: string): Observable<NycAuth> {
    return this.http.get<NycAuth>(`${environment.apiUrl}/api/private/users/${id}/nyc-auth`);
  }

  getNycApplicationOverrides(id: string): Observable<NycApplicationOverrideDto[]> {
    return this.http.get<NycApplicationOverrideDto[]>(
      `${environment.apiUrl}/api/private/users/${id}/nyc-application-overrides`
    );
  }

  upsertNycApplicationOverride(id: string, applicationId: string, command: UpsertNycApplicationOverrideCommand) {
    return this.http.put<NycApplicationOverrideDto>(
      `${environment.apiUrl}/api/private/users/${id}/nyc-application-overrides/${applicationId}`,
      command
    );
  }

  deleteNycApplicationOverride(id: string, applicationId: string) {
    return this.http.delete(
      `${environment.apiUrl}/api/private/users/${id}/nyc-application-overrides/${applicationId}`
    );
  }

  getAllNycAuth(): Observable<NycAuth[]> {
    return this.http.get<NycAuth[]>(`${environment.apiUrl}/api/private/users/nyc-auth`);
  }

  getRegistrationUsers(params: { view?: string; search?: string } = {}): Observable<RegistrationSearchResultDto> {
    return this.http.get<RegistrationSearchResultDto>(
      `${environment.apiUrl}/api/private/users/registration`,
      {params: {...params}},
    );
  }

  runAutoCreate(userIds: string[]): Observable<unknown> {
    return this.http.post(
      `${environment.apiUrl}/api/private/users/registration/run-auto-create`,
      {userIds}
    );
  }

  getActiveUsersNotRegistered() {
    return this.http.get<UserOverview[]>(`${environment.apiUrl}/api/private/users`, {
      params: {
        'not-registered': 'true'
      }
    });
  }

  getByEmail(email: string) {
    return this.http.get<UserOverview[]>(`${environment.apiUrl}/api/private/users`, {
      params: {
        email
      }
    });
  }

  getByStatus(statusOption: "signup-flow" | "mandatory-flow" | "active") {
    return this.http.get<UserOverview[]>(`${environment.apiUrl}/api/private/users`, {
      params: {
        status: statusOption
      }
    });
  }

  getActiveNotSyncedUsers() {
    return this.http.get<UserOverview[]>(`${environment.apiUrl}/api/private/users`, {
      params: {
        'not-synced': 'true'
      }
    });
  }

  getUsersAfterSignupFlow(search: string | undefined, view: string | undefined, fuzziness: number | undefined) {
    const params: Record<string, string> = {'after-signup-flow': 'true'};
    if (search && search.trim().length > 0) {
      params['search'] = search.trim();
    }
    if (view && view !== 'all') {
      params['view'] = view;
    }
    if (fuzziness !== undefined) {
      params['fuzziness'] = String(fuzziness);
    }
    return this.http.get<UserOverviewSearchResult>(`${environment.apiUrl}/api/private/users`, {params});
  }

  resetLoginSyncResult(id: string) {
    return this.http.put(`${environment.apiUrl}/api/private/users/${id}/reset-login-sync-result`, {});
  }

  markCurrentProfileComplete(id: string) {
    return this.http.put(`${environment.apiUrl}/api/private/users/${id}/mark-profile-complete`, {});
  }

  reconnectHousehold(id: string) {
    return this.http.put(`${environment.apiUrl}/api/private/users/${id}/reconnect-household`, {});
  }

  verifyNycHousehold(id: string) {
    return this.http.post<VerifyNycHouseholdResultDto>(`${environment.apiUrl}/api/private/users/${id}/verify-nyc-household`, {});
  }

  fixNycProfile(id: string) {
    return this.http.post<FixNycProfileResultDto>(`${environment.apiUrl}/api/private/users/${id}/fix-nyc-profile`, {});
  }

  markResetPassword(id: string) {
    return this.http.put(`${environment.apiUrl}/api/private/users/${id}/mark-reset-password`, {});
  }

  getActiveUsers() {
    return this.http.get<UserOverview[]>(`${environment.apiUrl}/api/private/users`);
  }

  getFailedWithChildren() {
    return this.http.get<UserOverview[]>(`${environment.apiUrl}/api/private/users/get-synced-with-children`, {});
  }

  getById(userId: string) {
    return this.http.get<UserOverview>(`${environment.apiUrl}/api/private/users/${userId}`, {});
  }

  unlockWaitingForEmailTwo(id: string) {
    return this.http.put(`${environment.apiUrl}/api/private/users/${id}/unlock-waiting`, {});
  }

  resetFailedApplications(id: string) {
    return this.http.put(`${environment.apiUrl}/api/private/users/${id}/reset-failed-applications`, {});
  }

  toggleIgnore(id: string, command?: ToggleIgnoreCommand) {
    return this.http.put(`${environment.apiUrl}/api/private/users/${id}/toggle-ignore`, command ?? {});
  }

  toggleSuppressUnknownEmails(id: string) {
    return this.http.put(`${environment.apiUrl}/api/private/users/${id}/toggle-suppress-unknown-emails`, {});
  }

  addFirstHouseholdMember(id: string) {
    return this.http.post(`${environment.apiUrl}/api/private/users/${id}/add-first-household-member`, {});
  }

  triggerScheduledResync(id: string) {
    return this.http.put(`${environment.apiUrl}/api/private/users/${id}/resync-applications`, {});
  }

  checkEligibility(id: string, command: CheckEligibilityCommand) {
    return this.http.put<CheckEligibilityResultDto>(`${environment.apiUrl}/api/private/users/${id}/check-eligibility`, command);
  }

  connectToNycApplication(id: string, command: ConnectToNycApplicationCommand) {
    return this.http.put(`${environment.apiUrl}/api/private/users/${id}/connect-nyc-application`, command);
  }

  addMissingNycIds(id: string) {
    return this.http.put(`${environment.apiUrl}/api/private/users/${id}/add-missing-nyc-ids`, {});
  }

  syncUserWithNyc(id: string) {
    return this.http.post<{ status: 'success' | 'skipped' | 'failed'; reason: string; applicationsApplied?: number }>(`${environment.apiUrl}/api/private/users/${id}/sync-with-nyc`, {})
  }

  markAsPaid(id: string, param: MarkAsPaidCommand) {
    return this.http.put(`${environment.apiUrl}/api/private/users/${id}/mark-as-paid`, param)
  }

  updateStripeStatus(id: string, command: UpdateStripeStatusCommand) {
    return this.http.put(`${environment.apiUrl}/api/private/users/${id}/update-stripe-status`, command)
  }

  sendCustomNotification(id: string, command: SendCustomNotificationCommand) {
    return this.http.post<{notificationId: string}>(`${environment.apiUrl}/api/private/users/${id}/custom-notifications`, command);
  }

  sendNotification(id: string, command: SendNotificationCommand) {
    return this.http.post<{notificationId: string}>(`${environment.apiUrl}/api/private/users/${id}/notifications`, command);
  }

  sendInAppNotification(id: string, command: SendInAppNotificationCommand) {
    return this.http.post<{notificationId: string}>(`${environment.apiUrl}/api/private/users/${id}/in-app-notifications`, command);
  }
}
